<template>
  <v-app>
    <div v-if="isLogin || consent">
      <sn-gradient-header
        :overlay-radius="24"
        :hide-back-btn="true"
        class="gradient-header"
        :class="($vuetify.breakpoint.smAndDown) ? 'sm' : ''"
      >
        <div class="pt-6 pt-sm-10 pb-5 px-4 px-sm-14">
          <br>
        </div>
      </sn-gradient-header>
      <login v-if="isLogin" />
      <consent
        v-else-if="consent"
      />
    </div>
    <no-access
      v-else-if="noAccess"
    />
    <div
      v-else
      class="app-container"
    >
      <sn-banner
        v-if="showBanner"
        :message="bannerText"
        type="warning"
        :primary-text="maySwitchToAdmin ? 'Switch to Admin view only' : ''"
        :secondary-text="maySwitchToProduction ? 'Switch to production' : ''"
        @primary-click="becomeSnAdmin"
        @secondary-click="toggleIsStaging"
      />
      <div
        class="flex evault-container"
        :style="showBanner ? 'margin-top: 72px;' : ''"
      >
        <sn-side-menu
          ref="sideMenu"
          :key="sideMenuKey"
          :header-image="ncinoLogo"
          :header-image-link="homeLink"
          subheader-text=""
          :items="sideNavOptions"
          :width="navWidth"
          :permanent="true"
          :brand-colors="{}"
          link-type="to"
          class="side-menu"
        >
          <template
            v-if="showViewingAs"
            v-slot:footer
          >
            <viewing-as
              ref="viewingAs"
              :user-name="userFullName"
            />
          </template>
        </sn-side-menu>
        <div class="content evault-content">
          <sn-header
            :key="headerValue"
            :header-text="headerText"
            :subtitle-text="subtitleText"
            :breadcrumb-items="breadcrumbs"
            :is-loading="$store.getters.showHeaderLoaderSkeleton"
            :tab-list="headerTabs"
            :activated-tab="activeTabIndex"
            data-cy="evault-header"
            @update-tab="toggleTabs"
          >
            <template v-slot:avatars>
              <sn-avatar
                v-if="userInitials !== ''"
                :image="user.profilePictureUrl"
                :initials="userInitials"
                class="mx-2"
                pointer-cursor
                data-cy="profile-icon"
                @click="showProfileDropdown()"
              />
              <profile-dropdown
                v-show="profileDropdownVisible"
                :key="orgGuid"
                :is-shown="profileDropdownVisible"
                :user-name="userFullName"
                :user-guid="user.guid"
                :profile-picture-url="user.profilePictureUrl"
                @close="closeProfileDropdown"
                @logout="logout"
              />
            </template>
          </sn-header>
          <router-view
            v-if="!loading"
            :key="routerKey"
            class="page-content"
          />
        </div>
        <evault-session-timeout-modal
          :display="timeoutModalVisible"
          :total-seconds="timeoutModalTotalSeconds"
          @extend="() => timeoutModalTotalSeconds = undefined"
        />
        <my-profile-nav-dialog
          v-model="profileSettingsVisible"
          :initial-tab="profileSettingsInitialTab"
          :user-guid="user.guid"
        />
        <failed-upload-docs-modal
          :toggle-modal="showFailedUploadDocsModal"
          :title="failedUploadDocsModalTitle"
          :message="failedUploadDocsModalMessage"
          :file-names="failedUploadDocsFileNames"
          @close="toggleFailedUploadDocsModal"
        />
        <error-overflow-modal
          :toggle-modal="showErrorOverflowModal"
          :title="errorOverflowModalTitle"
          :message="errorOverflow"
          @close="toggleErrorOverflowModal"
        />
      </div>
    </div>
    <sn-toaster
      v-if="evaultToastMixin_activeToasts.length > 0"
      :toasts="evaultToastMixin_activeToasts"
      @actionClicked="handleToastAction"
    />
  </v-app>
</template>

<script>
import ncinoLogo from '~/assets/images/NcinoEvault.svg'
import toastMixin from '../components/mixins/evaultToastMixin'
import ProfileDropdown from './evui/profileDropdown.vue'
import login from './login'
import noAccess from './noAccess.vue'
import consent from './consent.vue'
import { user } from './graphql/users/users_queries.gql'
import { company } from './graphql/companies_queries.gql'
import { orgs, routeOrg } from './graphql/org_queries.gql'
import myProfileNavDialog from './evui/myProfileNavDialog.vue'
import EvaultSessionTimeoutModal from './evui/evaultSessionTimeoutModal'
import ViewingAs from './evui/viewingAs'
import FailedUploadDocsModal from './evui/failedUploadDocsModal'
import ErrorOverflowModal from './evui/errorOverflowModal'
import snEvaultService from '../services/snEvaultService'
import _ from 'lodash'
import { mapGetters, mapMutations } from 'vuex'
import cookie from 'js-cookie'
import axios from 'axios'
import snThemeMixin from '~/components/common/snThemeMixin'

export default {
  name: 'EvaultApp',
  components: {
    EvaultSessionTimeoutModal,
    ViewingAs,
    FailedUploadDocsModal,
    ErrorOverflowModal,
    myProfileNavDialog,
    ProfileDropdown,
    login,
    noAccess,
    consent
  },
  mixins: [toastMixin, snThemeMixin],
  data () {
    return {
      headerValue: 0,
      navWidth: 256,
      profileDropdownVisible: false,
      showNotificationDropdown: false,
      showSnoozeModal: false,
      showViewingAs: true,
      isSnAdmin: null,
      activeTabIndex: 0,
      loading: true,
      tabActiveListener: null,
      windowResizeListener: null,
      trueViewingAsHeight: null,
      tabInactiveTimestamp: new Date().getTime(),
      timeoutModalTotalSeconds: undefined,
      showFailedUploadDocsModal: false,
      failedUploadDocsFileNames: [],
      errorOverflow: '',
      errorOverflowModalTitle: '',
      showErrorOverflowModal: false,
      initialTab: 0,
      homeLink: '/',
      ncinoLogo
    }
  },

  computed: {
    ...mapGetters(['user',
      'activeHeaderTab',
      'isStaging',
      'orgs',
      'orgGuid',
      'mersOrgId',
      'timeoutModalVisible',
      'viewAsCompany',
      'profileSettingsVisible',
      'profileSettingsInitialTab'
    ]),

    companyGuid () {
      if (this.user.isSnAdmin) {
        return this.viewAsCompany ? this.viewAsCompany.guid : undefined
      }
      return undefined
    },

    sideMenuKey () {
      // Force side menu to re-render when organization context changes
      return `${this.orgGuid}-${this.mersOrgId}-${this.viewAsCompany?.guid || 'no-company'}-${this.isStaging}`
    },

    bannerText () {
      if (this.isStaging && !this.viewAsCompany) {
        return 'You\'re in the Staging environment'
      } else if (this.isStaging) {
        return `You're viewing as ${this.viewAsCompany.name} ${this.viewAsCompany.locked && this.isSnAdmin ? '(Locked out)' : ''} in the Staging environment`
      } else if (this.viewAsCompany) {
        return `You're viewing as ${this.viewAsCompany.name} ${this.viewAsCompany.locked && this.isSnAdmin ? '(Locked out)' : ''}`
      }
      return ''
    },

    showBanner () {
      return this.isStaging || this.viewAsCompany
    },

    routerKey () {
      if (this.orgGuid != null && this.orgGuid !== 'undefined' && this.orgGuid !== '') {
        return this.orgGuid + this.isStaging
      }
      return this.isStaging
    },

    userFullName () {
      if (this.user) {
        return `${this.user.firstName || ''} ${this.user.lastName || ''}`
      }
      return ''
    },

    userInitials () {
      if (this.user) {
        const { firstName, lastName } = this.user
        return `${firstName ? firstName[0] : ''}${lastName ? lastName[0] : ''}`.toUpperCase()
      }
      return ''
    },

    isLogin () {
      return this.$route.path.includes('login')
    },

    noAccess () {
      return this.$route.path.includes('no_access')
    },

    consent () {
      return this.$route.path.includes('consent')
    },

    isEvaultRoot () {
      return this.$route.fullPath.replaceAll('/', '') === 'evault'
    },

    breadcrumbs () {
      if (this.isEvaultRoot) this.setBreadcrumbs([])
      return this.$store.getters.breadcrumbs
    },

    headerTabs () {
      if (this.isEvaultRoot) this.setHeaderTabs([])
      return this.$store.getters.headerTabs
    },

    maySwitchToAdmin () {
      return this.viewAsCompany
    },

    maySwitchToProduction () {
      return this.isStaging && snEvaultService.hasPermission(this.user, 'productionEnabled')
    },

    headerText () {
      if (this.$route.fullPath.replaceAll('/', '') === 'evault') {
        this.setHeaderText('Welcome to nCino eVault')
      }
      return this.$store.getters.headerText
    },
    subtitleText () {
      if (this.$route.fullPath.replaceAll('/', '') === 'evault') {
        this.setSubtitleText('Welcome to nCino eVault')
      }
      return this.$store.getters.subtitleText
    },
    sideNavOptions () {
      const enotePermissions = ['transfers', 'deliveries', 'changeStatus', 'changeData', 'register']
      const transactionPermissions = [...enotePermissions, 'inquiry', 'connectivity']
      const settingsPermissions = ['manageOrganizations', 'manageUsers', 'managePartners', 'manageRoles', 'viewAboutTab']
      const hasOrgGuid = this.orgGuid != null && this.orgGuid !== 'undefined' && this.orgGuid !== ''
      const hasMersOrgId = this.mersOrgId != null && this.mersOrgId !== 'undefined' && this.mersOrgId !== ''
      // For admin users, show org nav items if they have org context, regardless of viewAsCompany
      // For non-admin users, show nav items (they don't need viewAsCompany set)
      const hasCompanyOptions = this.isSnAdmin ? (hasOrgGuid && hasMersOrgId) : true

      const options = []

      if (transactionPermissions.some(permission => snEvaultService.hasPermission(this.user, permission)) && hasOrgGuid && hasMersOrgId && hasCompanyOptions) {
        options.push(
          {
            title: 'Transactions',
            icon: '$snExport',
            link: '/transactions'
          }
        )
      }
      if (enotePermissions.some(permission => snEvaultService.hasPermission(this.user, permission)) && hasOrgGuid && hasMersOrgId && hasCompanyOptions) {
        options.push(
          {
            title: 'eNote records',
            icon: '$snDollarHouse',
            link: '/enotes'
          }
        )
      }
      if (snEvaultService.hasPermission(this.user, 'electronicRecordsEnabled') && hasCompanyOptions) {
        options.push({
          title: 'Electronic records',
          icon: '$snFolder',
          link: '/electronic_records'
        })
      }
      if (snEvaultService.hasPermission(this.user, 'manageReports') && hasMersOrgId && hasCompanyOptions) {
        options.push(
          {
            title: 'Reports',
            icon: '$snDocumentImage',
            link: '/reports'
          }
        )
      }
      if (settingsPermissions.some(permission => snEvaultService.hasPermission(this.user, permission)) && hasCompanyOptions) {
        options.push(
          {
            title: 'Settings',
            icon: '$snSettings',
            link: '/company_settings'
          }
        )
      }
      if (this.isSnAdmin) {
        if (options.length) {
          options.push({ type: 'divider' })
        }
        options.push(...this.snAdminSideNavOptions)
      }
      const devHosts = ['localhost', 'ncmstaging.com', 'snstaging.com', 'ngrok']
      if (devHosts.some(host => window.location.host.includes(host))) {
        if (!this.isSnAdmin) {
          if (options.length) {
            options.push({ type: 'divider' })
          }
        }

        options.push({
          title: 'MERS tools',
          icon: '$snTools',
          link: '/mers_tools'
        })
      }
      return options
    },
    snAdminSideNavOptions () {
      const options = [
        {
          title: 'Companies',
          icon: '$snCity',
          link: '/companies'
        },
        {
          title: 'API Integrations',
          icon: '$snLinkVariant',
          link: '/integrations'
        }
      ]

      if (snEvaultService.hasSupportToolsPermission(this.user)) {
        options.push({
          title: 'Support tools',
          icon: '$snTools',
          link: '/support_tools'
        })
      }
      options.push({
        title: 'Data Dashboard',
        icon: '$snCase',
        link: '/data_dashboard'
      })
      return options
    },
    failedUploadDocsModalTitle () {
      return `${this.failedUploadDocsFileNames.length} ${this.failedUploadDocsFileNames.length === 1 ? 'document' : 'documents'} did not upload`
    },
    failedUploadDocsModalMessage () {
      return `${this.failedUploadDocsFileNames.length === 1 ? 'This document' : 'These documents'} failed to upload due to connection issues. Please try again.`
    },
    profileSettingsVisible: {
      get () {
        return this.$store.getters.profileSettingsVisible
      },
      set (value) {
        this.setProfileSettingsVisible(value)
      }
    }
  },

  watch: {
    $route () {
      this.resetAppTimeout()
      // Ensure organization context is set up when navigating to routes that require it
      this.$nextTick(async () => {
        if (this.$route.name && this.$route.name !== 'Login') {
          const requiresOrg = snEvaultService.requiresOrg(this.$route.name)
          if (requiresOrg) {
            await this.getRouteOrg()
          }
        }
      })
    },
    headerTabs () {
      this.headerValue++
    },
    activeHeaderTab () {
      this.activeTabIndex = this.headerTabs.map(tab => tab.key).indexOf(this.activeHeaderTab)
    },
    orgGuid () {
      // Force re-evaluation of side navigation when org context changes
      this.$forceUpdate()
    },
    viewAsCompany () {
      // Force re-evaluation of side navigation when company context changes
      this.$forceUpdate()
    }
  },

  async created () {
    if (this.isLogin) {
      return
    }

    this.loading = true
    const user = await this.getUser()

    if (!user) {
      return
    }
    const { guid, firstName, lastName, locked, profilePictureUrl, timezone, rawPermissions, transactionsRefreshInterval, company } = user
    const permissions = JSON.parse(rawPermissions) || {}
    const companyLocked = company.locked
    const featureFlags = JSON.parse(company.featureFlags) || {}

    /* camelCase and trim permission keys:
        permission_change_status_and_change_data => changeStatusAndChangeData
        access like: this.user.permissions.changeStatusAndChangeData */
    Object.keys(permissions).forEach(key => {
      const cleanKey = _.camelCase(key.replace('permission_setting', ''))
      permissions[cleanKey] = permissions[key]
      delete permissions[key]
    })

    const isSnAdmin = this.isSnAdmin
    this.setUser({ ...this.user, guid, firstName, lastName, locked, profilePictureUrl, timezone, isSnAdmin, permissions, transactionsRefreshInterval, company, companyLocked, featureFlags })
    await this.loadViewAsCompany()
    await this.getOrgs()

    if (cookie.get('tmp_evault_staging') != null) {
      this.setIsStaging(cookie.get('tmp_evault_staging') === true || cookie.get('tmp_evault_staging') === 'true')
      cookie.remove('tmp_evault_staging')
    } else if (isSnAdmin && !this.viewAsCompany) {
      this.setIsStaging(this.getDefaultStaging())
    }

    setTimeout(() => {
      this.loading = false
      this.setAppInitialized(true)
    }, 500)

    // 'visibililtychange' listens for when browser tab becomes (in)active
    this.tabActiveListener = document.addEventListener('visibilitychange', this.handleTabVisibilityChange)
    this.windowResizeListener = window.addEventListener('resize', this.handleWindowResize)
  },

  async mounted () {
    // Wait for router to be ready and route to be set
    if (this.$route.name && this.$route.name !== 'Login') {
      const requiresOrg = snEvaultService.requiresOrg(this.$route.name)
      if (requiresOrg) {
        // if the route requires an org, find the org and select it (regardless of current mersOrgId)
        await this.getRouteOrg()
      }

      // Wait a moment to ensure all state is properly set
      await this.$nextTick()

      const canAccess = snEvaultService.canAccess(this.user, this.$route.name, !!this.mersOrgId)
      if (!canAccess) {
        this.$router.push(snEvaultService.getDefaultPage(this.user, !!this.mersOrgId))
      }
    }
  },

  beforeDestroy () {
    document.removeEventListener('visibilitychange', this.tabActiveListener)
    window.removeEventListener('resize', this.windowResizeListener)
  },

  methods: {
    ...mapMutations([
      'becomeCompany',
      'resetAppTimeout',
      'setActiveHeaderTab',
      'setAppInitialized',
      'setBreadcrumbs',
      'setHeaderTabs',
      'setHeaderText',
      'setIsStaging',
      'setOrgs',
      'setMersOrgId',
      'setOrgGuid',
      'setUser',
      'setSubtitleText',
      'setTimeoutModalVisible',
      'setProfileSettingsVisible'
    ]),

    async getUser () {
      const { data } = await this.$apollo.query({
        query: user,
        variables: {}
      })

      if (data.user == null) {
        this.setUser({ loggedIn: false, permissions: {} })
      } else {
        this.isSnAdmin = data.user.admin
        this.setUser({ ...data.user, permissions: {}, loggedIn: true })
      }
      return data.user
    },

    async getRouteOrg () {
      const { data } = await this.$apollo.query({
        query: routeOrg,
        variables: {
          route: this.$route.name,
          guid: this.$route.params.guid,
          isTest: this.isStaging
        },
        fetchPolicy: 'network-only'
      })

      if (data.routeOrg) {
        if (data.routeOrg.guid) {
          this.setOrgGuid(data.routeOrg.guid)
        }
        if (data.routeOrg.remoteId) {
          this.setMersOrgId(data.routeOrg.remoteId)
        }
        const chartContextCookie = cookie.get('tmp_evault_chart_to_report_context')
        let appliedFromChartContext = false
        if (chartContextCookie) {
          try {
            const ctx = JSON.parse(chartContextCookie)
            if (ctx.reportGuid && ctx.reportGuid === this.$route.params.guid) {
              this.setIsStaging(ctx.isTest === true || ctx.isTest === 'true')
              appliedFromChartContext = true
            }
          } catch (e) {
            // fall through to default
          }
        }
        if (!appliedFromChartContext) {
          this.setIsStaging(data.routeOrg.isTest)
        }
        if (this.user.isSnAdmin && data.routeOrg.company != null && this.$route.query.from !== 'data_dashboard') {
          this.becomeCompany(data.routeOrg.company)
          // Also set sessionStorage for admin access control
          sessionStorage.setItem('evault_view_as_company_guid', data.routeOrg.company.guid)
        }
      }
    },

    async getOrgs () {
      if (this.user.isSnAdmin && !this.companyGuid) {
        this.setOrgGuid(null)
        this.setMersOrgId(null)
        return
      }

      const { data } = await this.$apollo.query({
        query: orgs,
        variables: {
          companyGuid: this.companyGuid
        }
      })

      if (data.orgs) {
        this.setOrgs(data.orgs.nodes)
        const orgGuids = this.orgs.map(org => org.guid)
        const orgIds = this.orgs.map(org => org.remoteId)
        const cookieOrgGuid = cookie.get('evault_org_guid')
        const cookieMersOrgId = cookie.get('evault_mers_org_id') || cookie.get('tmp_evault_org_id')

        if (orgGuids.includes(cookieOrgGuid)) {
          this.setOrgGuid(cookieOrgGuid)
          this.setMersOrgId(cookieMersOrgId)
        } else if (orgGuids.length > 0) {
          this.setOrgGuid(orgGuids[0])
          this.setMersOrgId(orgIds[0])
        } else {
          this.setOrgGuid(null)
          this.setMersOrgId(null)
        }
      }
    },

    async loadViewAsCompany () {
      if (!this.isSnAdmin) {
        this.becomeCompany(null)
        return null
      }
      // Using a cookie so that when user opens a new tab they still have a way to load the company guid. For example, link in monolith.
      const viewAsCompanyGuid = sessionStorage.getItem('evault_view_as_company_guid') || cookie.get('tmp_evault_company_guid')
      cookie.remove('tmp_evault_company_guid')

      if (viewAsCompanyGuid) {
        const { data } = await this.$apollo.query({
          query: company,
          variables: {
            companyGuid: viewAsCompanyGuid
          }
        })
        this.becomeCompany(data.company)

        const mersOrgId = sessionStorage.getItem('evault_mers_org_id') || cookie.get('tmp_evault_org_id') || cookie.get('evault_mers_org_id')
        cookie.remove('tmp_evault_org_id')
        this.setMersOrgId(mersOrgId)
      }
    },

    showProfileDropdown () {
      this.resetAppTimeout()
      if (!this.profileDropdownVisible) {
        this.profileDropdownVisible = true
      }
    },

    toggleIsStaging () {
      const matchedPath = this.$route.matched[0].path
      const basePath = matchedPath.split(':')[0]
      this.setIsStaging(!this.isStaging)

      if (basePath !== matchedPath) {
        this.$router.push(basePath)
      }
    },

    getDefaultStaging () {
      return process.env.RAILS_ENV === 'development' || process.env.RAILS_ENV === 'staging'
    },

    becomeSnAdmin () {
      this.$router.push('/companies').then(() => {
        this.becomeCompany(null)
        this.setIsStaging(this.getDefaultStaging())
        this.setOrgGuid(null)
        this.setMersOrgId(null)
      })
    },

    closeProfileDropdown () {
      this.resetAppTimeout()
      this.showNotificationDropdown = false
      this.profileDropdownVisible = false
    },

    logout () {
      sessionStorage.clear()
      window.location.assign('/logout')
    },

    toggleTabs (tabId) {
      this.activeTabIndex = this.headerTabs.map(tab => tab.id).indexOf(tabId)
      const tab = this.headerTabs[this.activeTabIndex]
      this.setActiveHeaderTab(tab.key)
      if (tab.breadcrumb != null) {
        const breadcrumbs = this.$store.getters.breadcrumbs
        if (breadcrumbs != null && breadcrumbs.length > 1) {
          breadcrumbs.pop()
        }
        breadcrumbs.push({ text: tab.breadcrumb })
        this.setBreadcrumbs(breadcrumbs)
      }
    },

    async handleTabVisibilityChange () {
      if (document.visibilityState === 'visible') {
        const authIsValid = await this.getAuthStatus()
        if (!authIsValid) {
          this.logout()
        }

        const now = new Date().getTime()
        const minutesInactive = (now - this.tabInactiveTimestamp) / 60000

        if (minutesInactive > 15) {
          this.logout()
        } else if (minutesInactive > 10) {
          this.timeoutModalTotalSeconds = Math.floor((15 - minutesInactive) * 60)
          this.setTimeoutModalVisible(true)
        } else {
          const minutesUntilModalShows = 10 - minutesInactive
          this.resetAppTimeout(minutesUntilModalShows)
        }
      } else {
        this.tabInactiveTimestamp = new Date().getTime()
      }
    },

    handleWindowResize () {
      const fullSideMenu = this.$refs.sideMenu.$el
      const viewingAs = this.$refs.viewingAs?.$el
      if (viewingAs) {
        // trueViewingAsHeight is the height of the viewingAs element if it's ever rendered.
        // we still get into this block if the viewingAs element is hidden, so we save the height while we have it
        // so that we can determine if we have enough room to show it again
        this.trueViewingAsHeight = viewingAs.clientHeight
      }

      const viewingAsHeight = viewingAs?.clientHeight || this.trueViewingAsHeight || 130
      const fullSideMenuHeight = fullSideMenu.clientHeight
      const spaceForItems = this.sideNavOptions.length * 65

      if (fullSideMenuHeight - spaceForItems < viewingAsHeight) {
        this.showViewingAs = false
      } else {
        this.showViewingAs = true
      }
    },

    async getAuthStatus () {
      const authGuid = cookie.get('evault_auth_guid')
      const { data } = await axios.post('/auth/v1/sync', { auth_guid: authGuid })
      return data.success
    },

    handleToastAction (data) {
      switch (data.dataSource) {
        case 'uploadDocs':
          this.failedUploadDocsFileNames = data.data
          this.toggleFailedUploadDocsModal()
          break
        case 'errorOverflow':
          this.errorOverflow = data.data
          this.errorOverflowModalTitle = data.title
          this.toggleErrorOverflowModal()
          break
        case 'attachToEnote':
          this.$router.push(`/enotes/${data.data}`)
          break
        case 'detachFromEnote':
          this.$router.push(`/electronic_records/${data.data}`)
          break
        case 'editReport':
          this.$router.push(`/reports/${data.data}`)
          break
        default:
          break
      }
    },

    toggleFailedUploadDocsModal () {
      this.showFailedUploadDocsModal = !this.showFailedUploadDocsModal
    },

    toggleErrorOverflowModal () {
      this.showErrorOverflowModal = !this.showErrorOverflowModal
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~/assets/css/snui/index';

.content {
  width: 100vw;
}

.page-content {
  z-index: 100;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: auto;
  overscroll-behavior: none;
}

.flex {
  display: flex;
}

.gradient-header.sm {
  height: 80px !important;
}
.gradient-header:not(.sm) {
  height: 230px !important;
}

.evault-container {
  height: 100%;
  overflow: hidden;
}

.evault-content {
  overflow: auto;
}
</style>
